import type { FC, ReactNode } from 'react';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import { TbFilter } from 'react-icons/tb';
import { Box, Typography } from '@mui/material';

import { useFilterContext } from 'src/components/app/context/FilterContext';

import { AccordionItemContext } from '../AccordionItem';

import { styles } from './index.styles';

interface AccordionItemHeaderProps {
  headline: string;
  nested?: boolean;
  children?: ReactNode;
  showSelectedFilterCountForKey?: string[];
  selectedValueToShow?: string;
}

const AccordionItemHeader: FC<AccordionItemHeaderProps> = ({
  headline,
  nested,
  children,
  showSelectedFilterCountForKey,
  selectedValueToShow,
}) => {
  const { t } = useTranslation('common');
  const { isActive } = useContext(AccordionItemContext);
  const { filterGroups } = useFilterContext();

  const selectedFilterCount = useMemo(() => {
    if (!showSelectedFilterCountForKey) return 0;
    let count = 0;

    for (const key of showSelectedFilterCountForKey) {
      if (key in filterGroups) {
        count += filterGroups[key].length;
      }
    }

    return count;
  }, [filterGroups, showSelectedFilterCountForKey]);

  return (
    <Box sx={styles.header}>
      <Typography
        variant="body1"
        component={nested ? 'h4' : 'h3'}
        sx={{
          fontWeight: nested ? '400' : '600',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          userSelect: 'none',
        }}
      >
        {nested && isActive && <FaMinusCircle size="16" />}
        {nested && !isActive && <FaPlusCircle size="16" />}
        {headline}
      </Typography>
      {(showSelectedFilterCountForKey || selectedValueToShow) && (
        <Typography variant="caption" sx={styles.selectedFilterCount}>
          {selectedValueToShow ||
            t('common:nSelected', { count: selectedFilterCount })}
        </Typography>
      )}
      {children && <Box sx={styles.children}>{children}</Box>}
      {!nested && (
        <Box sx={[styles.icon, isActive && styles.iconActive]}>
          <TbFilter />
        </Box>
      )}
    </Box>
  );
};
export default AccordionItemHeader;
